/**
 * Helpers for the Admin → Legal documents tab.
 */

/**
 * Short date for admin lists, e.g. "Sep 29, 2026"; "—" when missing.
 *
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export const formatShortDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * "1 acceptance" / "12 acceptances".
 *
 * @param {number|null|undefined} count
 * @returns {string}
 */
export const acceptanceLabel = (count) => {
  const n = Number(count) || 0;
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'acceptance' : 'acceptances'}`;
};

const VERSION_PATTERN = /^(.*?)(\d+)\.(\d+)(.*)$/;

/**
 * Suggest the next version label for a document: bump the minor number of the
 * highest "X.Y" version seen, keeping its prefix/suffix ("draft-v0.1" →
 * "draft-v0.2", "1.0" → "1.1"), skipping labels already taken.
 *
 * @param {Array<{version: string}>} versions - Every version of one document.
 * @returns {string}
 */
export const suggestNextVersion = (versions = []) => {
  const taken = new Set(versions.map((v) => String(v.version)));
  let best = null;
  versions.forEach((v) => {
    const match = String(v.version).match(VERSION_PATTERN);
    if (!match) return;
    const major = Number(match[2]);
    const minor = Number(match[3]);
    if (!best || major > best.major || (major === best.major && minor > best.minor)) {
      best = { prefix: match[1], major, minor, suffix: match[4] };
    }
  });
  if (!best) best = { prefix: 'draft-v', major: 0, minor: 0, suffix: '' };

  let minor = best.minor + 1;
  let candidate = `${best.prefix}${best.major}.${minor}${best.suffix}`;
  while (taken.has(candidate)) {
    minor += 1;
    candidate = `${best.prefix}${best.major}.${minor}${best.suffix}`;
  }
  return candidate;
};

/**
 * Split one document's versions into the live version, the open draft, and
 * the published history (newest first, live one included).
 *
 * @param {Array<object>} versions - All versions (any doc), newest first.
 * @param {string} docKey - 'privacy' | 'terms'.
 * @returns {{all: Array<object>, current: object|null, draft: object|null, published: Array<object>}}
 */
export const groupVersions = (versions, docKey) => {
  const all = versions.filter((v) => v.doc_key === docKey);
  const published = all.filter((v) => v.status === 'published');
  const current = published.find((v) => v.is_current) || null;
  const draft = all.find((v) => v.status === 'draft') || null;
  return { all, current, draft, published };
};
